import { Injectable } from '@angular/core'
import { Actions, Effect, ofType } from '@ngrx/effects'
import { select, Store } from '@ngrx/store'
import {
  ActionFailed,
  ActionSkipped,
  loadRentalComplete,
  loadRentalsComplete,
  selectOnce,
  Toaster,
  updateMultipleRentalsComplete,
} from '@tokeet/tv3-platform'
import { of } from 'rxjs'
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators'
import { LoadEntityTags, LoadEntityTagsComplete, SetEntityTags, SetEntityTagsComplete } from './actions'
import { EntityTagsResponse } from './model'
import { selectAllEntityTags, selectTagsEntity } from './selectors'
import { EntityTagsService } from './service'
import { entityTagsToRentalUpdates, resolveSetEntityTagsOperation } from './tag-sync'

@Injectable()
export class EntityTagsEffects {
  @Effect()
  load$ = this.actions$.pipe(
    ofType(LoadEntityTags),
    switchMap(() =>
      this.tagsService.getAllTags().pipe(
        map((items) => LoadEntityTagsComplete({ items })),
        catchError((error) => of(new ActionFailed(error)))
      )
    )
  )

  @Effect()
  set$ = this.actions$.pipe(
    ofType(SetEntityTags),
    switchMap((data) =>
      this.store.pipe(
        selectOnce(selectTagsEntity(data.entityType, data.entityId)),
        switchMap((entity: EntityTagsResponse | undefined) => {
          if (resolveSetEntityTagsOperation(data.tags, entity) === 'skip') {
            return of(new ActionSkipped())
          }

          return this.tagsService.updateTags(data.entityType, data.entityId, data.tags).pipe(
            map((item) => SetEntityTagsComplete({ item })),
            tap(() => !data.silent && this.toaster.success('Tags updated successfully')),
            catchError((error) => of(new ActionFailed(error)))
          )
        })
      )
    )
  )

  @Effect()
  syncRentalTags$ = this.actions$.pipe(
    ofType(LoadEntityTagsComplete, SetEntityTagsComplete, loadRentalComplete, loadRentalsComplete),
    withLatestFrom(this.store.pipe(select(selectAllEntityTags))),
    map(([, items]) => updateMultipleRentalsComplete({ updates: entityTagsToRentalUpdates(items) }))
  )

  constructor(
    private actions$: Actions,
    private store: Store<any>,
    private toaster: Toaster,
    private tagsService: EntityTagsService
  ) {}
}
